import { useState, useEffect, useMemo } from 'react';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
    FileText, 
    MoreVertical, 
    Printer, 
    Eye, 
    Calendar,
    Users,
    CreditCard,
    LayoutDashboard,
    Trash2
} from 'lucide-react';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

interface InvoiceListProps {
  onSelect: (id: string) => void;
  limit?: number;
}

export function InvoiceList({ onSelect, limit }: InvoiceListProps) {
  const { invoices, isLoading, fetchInvoices, archiveInvoice } = useInvoiceStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = useMemo(() => {
    let result = (invoices || []).filter(inv => 
        (inv.invoiceNumber || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (inv.clientInfo.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (limit) result = result.slice(0, limit);
    return result;
  }, [invoices, searchQuery, limit]);

  const StatusBadge = ({ status }: { status: string }) => {
    const variants: Record<string, string> = {
      draft: 'bg-slate-500/10 text-muted-foreground border-slate-500/20',
      issued: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      paid: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      partially_paid: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      cancelled: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
      overdue: 'bg-rose-600 text-foreground border-transparent shadow-lg shadow-rose-600/20',
    };

    return (
      <Badge
        className={cn(
          "text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded border",
          variants[status] || 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-white/5'
        )}
      >
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="bg-muted/50 border border-border h-48 animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-widest">Invoices</h2>
            {filteredInvoices.length > 0 && (
                <div className="hidden sm:flex items-center gap-2 px-2 py-1 bg-muted/50 border border-border rounded-lg">
                    <span className="text-emerald-500 text-[8px] font-semibold uppercase tracking-widest whitespace-nowrap">
                        {filteredInvoices.length} Results
                    </span>
                </div>
            )}
        </div>
        <div className="relative group max-w-xs">
          <div className="relative flex items-center bg-muted/50 border border-border rounded-lg px-3 py-1.5 focus-within:border-emerald-500/30 transition-all shadow-sm">
            <FileText className="h-3.5 w-3.5 text-emerald-500/60 mr-2" />
            <input 
              type="text" 
              placeholder="Search invoices…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground placeholder:text-zinc-500 w-full"
            />
          </div>
        </div>
      </div>

      {/* Invoice List — Cardless on desktop, Contained on mobile */}
      <motion.div 
        layout
        initial="hidden"
        animate="show"
        variants={{
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: {
              staggerChildren: 0.05
            }
          }
        }}
        className="flex flex-col gap-4 md:gap-0 md:border md:border-zinc-200 md:dark:border-white/5 md:rounded-2xl md:bg-white/50 md:dark:bg-zinc-950/50 md:backdrop-blur-sm overflow-hidden"
      >
        {/* Table Header (Desktop Only) */}
        {filteredInvoices.length > 0 && (
          <div className="hidden md:flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
            <div className="w-1/3 flex items-center gap-6">
              <div className="w-24 shrink-0">
                <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Status</span>
              </div>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Invoice Details</span>
            </div>
            <div className="w-1/4 flex items-center justify-center">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Client & Date</span>
            </div>
            <div className="w-1/4 flex items-center justify-end pr-14">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Amount</span>
            </div>
          </div>
        )}
        <AnimatePresence mode="popLayout">
          {filteredInvoices.length === 0 ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="h-[200px] flex flex-col items-center justify-center bg-[#0a0a0c]/40 rounded-[24px] border border-dashed border-border backdrop-blur-sm m-4"
            >
              <div className="h-12 w-12 rounded-xl bg-muted/50 border border-border flex items-center justify-center mb-3">
                  <FileText className="h-5 w-5 text-slate-800" />
              </div>
              <p className="text-slate-700 font-semibold uppercase tracking-[0.4em] text-[9px]">No Fiscal Records Identified</p>
            </motion.div>
          ) : (
            filteredInvoices.map((invoice) => (
              <motion.div
                key={invoice.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="group cursor-pointer"
                onClick={() => onSelect(invoice.id)}
              >
                <div className="
                  bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-white/5 rounded-xl shadow-black/5 p-4
                  md:bg-transparent md:dark:bg-transparent md:border-transparent md:shadow-none md:rounded-none md:p-4
                  md:flex md:items-center md:justify-between md:border-b md:border-zinc-100 md:dark:border-white/[0.04] md:last:border-b-0
                  hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors
                ">
                  {/* Left Side: Title & Status */}
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 md:w-1/3">
                    <div className="flex justify-between items-center md:hidden mb-2">
                      <StatusBadge status={invoice.status} />
                    </div>
                    <div className="hidden md:block w-24 shrink-0">
                      <StatusBadge status={invoice.status} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight line-clamp-1 group-hover:text-emerald-500 transition-colors">
                        {invoice.invoiceNumber || '[DRAFT INVOICE]'}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest">#{invoice.id.slice(0, 8)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle: Client & Date */}
                  <div className="mt-4 md:mt-0 pt-4 md:pt-0 border-t border-zinc-100 dark:border-white/5 md:border-t-0 flex md:flex-col justify-between md:justify-center gap-2 md:w-1/4">
                    <div className="flex items-center gap-2">
                      <Users className="h-3 w-3 text-zinc-400" />
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 truncate">
                        {invoice.clientInfo.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500">
                      <Calendar className="h-3 w-3" />
                      <span className="text-[10px] font-medium uppercase tracking-widest">
                        {invoice.issuedAt ? format(new Date(invoice.issuedAt), 'dd MMM yy') : 'NOT ISSUED'}
                      </span>
                    </div>
                  </div>

                  {/* Right: Valuation & Actions */}
                  <div className="mt-4 md:mt-0 flex items-center justify-between md:justify-end gap-6 md:w-1/4">
                    <div className="text-left md:text-right">
                      <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest mb-0.5">Gross Total</p>
                      <p className="text-sm font-semibold tabular-nums tracking-tight text-zinc-900 dark:text-zinc-100 group-hover:text-emerald-500 transition-colors">
                        {formatCurrency(invoice.financials.grandTotal)}
                      </p>
                      {invoice.financials.paidAmount > 0 && (
                        <p className="text-[10px] font-medium tabular-nums text-emerald-600 mt-0.5">
                            Collected: {formatCurrency(invoice.financials.paidAmount)}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-foreground hover:bg-muted/50 rounded-md" onClick={(e) => e.stopPropagation()}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-white/5 text-zinc-900 dark:text-zinc-100 rounded-xl shadow-lg">
                          <DropdownMenuItem onClick={() => onSelect(invoice.id)} className="text-[10px] font-medium uppercase tracking-widest py-2 px-3 focus:bg-zinc-100 dark:focus:bg-zinc-800 rounded-lg cursor-pointer">
                            <Eye className="h-3.5 w-3.5 mr-2 text-zinc-500" /> Details
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-[10px] font-medium uppercase tracking-widest py-2 px-3 focus:bg-zinc-100 dark:focus:bg-zinc-800 rounded-lg cursor-pointer">
                            <Printer className="h-3.5 w-3.5 mr-2 text-zinc-500" /> Print PDF
                          </DropdownMenuItem>
                          {(invoice.status === 'draft' || invoice.status === 'cancelled') && (
                            <DropdownMenuItem 
                              onClick={async (e) => {
                                e.stopPropagation();
                                const actionVerb = invoice.status === 'draft' ? 'discard' : 'archive';
                                const actionNoun = invoice.status === 'draft' ? 'draft' : 'cancelled invoice';
                                if (window.confirm(`Are you sure you want to ${actionVerb} this ${actionNoun}?`)) {
                                  try {
                                    await archiveInvoice(invoice.id);
                                    toast.success(`${invoice.status === 'draft' ? 'Draft' : 'Cancelled invoice'} archived successfully`);
                                  } catch (err: unknown) {
                                    toast.error('Failed to archive: ' + (err instanceof Error ? err.message : 'Unknown error'));
                                  }
                                }
                              }} 
                              className="text-[10px] font-medium uppercase tracking-widest py-2 px-3 text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-900/20 rounded-lg cursor-pointer"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> {invoice.status === 'draft' ? 'Discard' : 'Archive'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
